import { Link, useNavigate } from "react-router-dom";
import { Monitor, Users, Calendar, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useCart, type CartItem } from "../utils/cartContext";
import { useAuth } from "@/hooks/useAuth";
import { formatDisplayDate } from "../utils/validationUtils";

interface Inventory {
  // Core identifiers
  id: string;
  dealId: string;
  inventoryId: string;
  inventoryReferenceId: string;
  mediaOwnerName: string;

  // Display information
  name: string;
  displayName: string;
  description: string;

  // Pricing
  price: any;
  currentPrice: any;
  nowPayPrice: any;
  perSpotPrice: any;
  perDayPrice: any;

  // Location & Images
  stateName: string;
  districtName?: string;
  inventoryAddress: string;
  fullResImagePath: string;
  thumbnailPath: string;
  inventoryThumbnailUrl: string;

  // Technical specifications
  inventoryType: string;
  inventoryResolution: string;
  inventoryVenueType: string;
  type: any;
  specification: any;

  // Dimensions
  resolutionHeight: any;
  resolutionWidth: any;
  panelHeight: any;
  panelWidth: any;
  totalSize: number;
  sizeResolution: string;
  screenCount: number;

  // Availability
  availableBooking?: boolean;
  nextAvailableDate: any;
  active?: boolean;
  reasonForRejection: string;

  // Performance metrics
  inventoryReports: {
    totalPotentialViews: number;
  };
  monthlySummary: {
    totalVisitors: number;
    uniqueVisitors: number;
    frequency: number;
  };
  sellingRate: {
    daily: any;
    spots: any;
    monthly: {
      month1: number;
    };
  };

  // Geographic coordinates
  inventoryLatitude?: number;
  inventoryLongitude?: number;
  latitude?: number;
  longitude?: number;

  // Cart state
  isInCart?: boolean;
}

interface BillboardCardProps {
  billboard: Inventory;
  currencyCode?: string;
  handleInventorySelect?: (inventoryItem: Inventory) => void;
  countryOptions?: any[];
  selectedCountryId?: string;
  handleAddToCart?: (inventoryItem: Inventory) => void;
  handleRemoveFromCart?: (inventoryItem: Inventory) => void;
  isInCart?: boolean;
}

export default function BillboardCard({ billboard, currencyCode, handleInventorySelect, countryOptions, selectedCountryId, handleAddToCart, handleRemoveFromCart, isInCart }: BillboardCardProps) {
  const { toast } = useToast();
  const { addToCart, removeFromCart, isInCart: checkIsInCart, priceFormat } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const actuallyInCart = checkIsInCart(billboard.id);

  // If this is a package, don't render - packages use PackageCard component
  if (billboard.inventoryType === "Package") {
    return null;
  }

  // const handleAddToCart = () => {
  //   let price = 0;
  //   if (billboard.type === 'DIGITAL') {
  //     price = priceFormat === 'spot-based'
  //       ? billboard.perSpotPrice
  //       : billboard.perDayPrice;
  //   } else {
  //     // For classic/non-digital
  //     price = (billboard.sellingRate?.monthly?.month1 && billboard.sellingRate.monthly.month1 > 0)
  //       ? billboard.sellingRate.monthly.month1
  //       : billboard.currentPrice;
  //   }

  //   const cartItem: CartItem = {
  //     id: billboard.id, // Keep as string to match cart context expectations
  //     name: billboard.displayName || billboard.name,
  //     location: billboard.stateName || billboard.inventoryAddress,
  //     state: billboard.stateName || billboard.inventoryAddress,
  //     price: Number(price) * 100,
  //     impressions: billboard.inventoryReports?.totalPotentialViews || billboard.monthlySummary?.totalVisitors || 0,
  //     uniqueVisitors: billboard.monthlySummary?.uniqueVisitors || 0,
  //     frequency: billboard.monthlySummary?.frequency || 0,
  //     size: (billboard.panelWidth && billboard.panelHeight
  //       ? `${billboard.panelWidth}x${billboard.panelHeight}`
  //       : 'Standard'),
  //     type: billboard.inventoryType || billboard.type || 'Classic', // Default to Classic
  //     status: billboard.availableBooking ? "available" : "sold-out",
  //     image: billboard.fullResImagePath &&
  //       billboard.fullResImagePath !== 'null' &&
  //       billboard.fullResImagePath.trim() !== ''
  //       ? billboard.fullResImagePath
  //       : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
  //     currencyCode: billboard?.price?.localCurrencies?.code || 'N/A',
  //     // Attach country information to billboard item
  //     countryId: selectedCountryId,
  //     countryName: countryOptions.find((c: any) => c.id === selectedCountryId)?.label || localStorage.getItem('sessionDefaultCountryName') || 'N/A',
  //     specifications: {
  //       resolution: (
  //         billboard.type === 'CLASSIC'
  //           ? 'N/A'
  //           : (billboard.resolutionWidth && billboard.resolutionHeight
  //             ? `${billboard.resolutionWidth}x${billboard.resolutionHeight}`
  //             : (billboard.specification?.resolution1Width && billboard.specification?.resolution1Height
  //               ? `${billboard.specification.resolution1Width}x${billboard.specification.resolution1Height}`
  //               : (billboard.sizeResolution)))
  //       ), duration: billboard.inventoryType === "Classic" ? "30 days" : "Variable"
  //     },
  //     priceFormat: priceFormat || 'N/A',
  //     isSpotBased: (priceFormat === 'spot-based') ? true : false,
  //   };

  //   addToCart(cartItem);
  //   toast({
  //     title: "Added to cart",
  //     description: "Billboard has been added to your cart successfully.",
  //   });
  // };

  // const handleRemoveFromCart = () => {
  //   removeFromCart(billboard.id);
  //   toast({
  //     title: "Removed from cart",
  //     description: "Billboard has been removed from your cart.",
  //   });
  // };

  const handleViewDetails = (inventoryItem: any) => {


    // Get the billboard type from the inventory item
    const billboardType = inventoryItem.inventoryType || inventoryItem.type || 'digital';

    let currentPrice = 0;
    if (billboard.type === 'DIGITAL') {
      currentPrice = priceFormat === 'spot-based'
        ? billboard.perSpotPrice
        : billboard.perDayPrice;
    } else {
      // For classic/non-digital
      currentPrice = (billboard.sellingRate?.monthly?.month1 && billboard.sellingRate.monthly.month1 > 0)
        ? billboard.sellingRate.monthly.month1
        : billboard.currentPrice;
    }

    navigate(`/billboard/${inventoryItem.id}?type=${encodeURIComponent(billboardType)}`, {
      state: {
        currentPrice: currentPrice
      }
    });
  };
  return (
    <Card className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
      <div className="relative">
        {/* <Badge
          className={`absolute top-3 left-3 z-10 ${billboard.availableBooking ? 'bg-emerald-400 text-white' : 'bg-red-500 text-white'
            }`}
        >
          <div className="w-2 h-2 rounded-full bg-current mr-1"></div>
          {billboard.availableBooking ? 'Available' : 'Sold Out'}
        </Badge> */}
        {actuallyInCart && (
          <Badge className="absolute top-3 right-3 z-10 bg-emerald-600 text-white">
            In Cart
          </Badge>
        )}

        <img
          src={billboard.fullResImagePath &&
            billboard.fullResImagePath !== 'null' &&
            billboard.fullResImagePath.trim() !== ''
            ? billboard.fullResImagePath
            : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`}
          alt={billboard.displayName || billboard.name}
          className="w-full h-40 object-cover"
          onError={(e) => {
            e.currentTarget.src = `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`;
          }}
        />
      </div>

      <CardContent className="p-4">
        <div className="mb-3">
          <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-1" title={billboard.displayName || billboard.name}>
            {billboard.displayName || billboard.name}
          </h3>
          <div className="flex items-center gap-1 mb-1">
            {billboard.districtName && (
              <p className="text-sm text-gray-600">{billboard.districtName}</p>
            )}
            {billboard.districtName && billboard.stateName && (
              <span className="text-sm text-gray-600">,</span>
            )}
            {billboard.stateName && (
              <p className="text-sm text-gray-600">{billboard.stateName}</p>
            )}
            {!billboard.districtName && !billboard.stateName && (
              <p className="text-sm text-gray-600">{billboard.inventoryAddress}</p>
            )}
          </div>
          {/* {billboard.mediaOwnerName && (
            <Link
              to={`/store/${billboard.mediaOwnerName}`}
              className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline font-medium mb-2 inline-block"
              onClick={() => {
                // Set back URL context based on current page
                const currentPath = window.location.pathname;
                sessionStorage.setItem('mediaOwnerBackUrl', currentPath);
              }}
            >
              by {billboard.mediaOwnerName}
            </Link>
          )} */}
          <span className='text-xs text-emerald-600 hover:text-emerald-700 font-medium mb-2 inline-block'>
            by {billboard.mediaOwnerName ?? 'N/A'}
          </span>

          <div className="text-lg font-bold text-emerald-600">
            {(() => {
              let price = 0;
              let suffix = '';

              if (billboard?.type === 'DIGITAL') {
                if (priceFormat === 'spot-based') {
                  price = billboard?.perSpotPrice;
                  suffix = '/Spot';
                } else if (priceFormat === 'day-based') {
                  price = billboard?.perDayPrice;
                  suffix = '/Day';
                }
              } else {
                // For classic/non-digital
                if (billboard?.sellingRate?.monthly?.month1 && billboard.sellingRate.monthly.month1 > 0) {
                  price = billboard.sellingRate.monthly.month1;
                  suffix = '/Month';
                } else {
                  price = billboard?.currentPrice;
                  suffix = '/Month';
                }
              }

              if (!price || price === 0) return 'N/A';

              const displayCurrencyCode = billboard?.price?.localCurrencies?.code || currencyCode || 'N/A';

              return `${displayCurrencyCode} ${price.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}${suffix}`;
            })()}
          </div>

        </div>

        {isAuthenticated ? (
          <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
            <div className="flex items-center">
              <Monitor className="mr-1 h-3 w-3" />
              <span>{billboard.sizeResolution}</span>
            </div>
            <div className="flex items-center">
              <Users className="mr-1 h-3 w-3" />
              <span>{billboard.screenCount}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="mr-1 h-3 w-3" />
              <span>{billboard?.nextAvailableDate ? formatDisplayDate(billboard.nextAvailableDate) : 'N/A'}</span>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-center text-xs text-gray-600">
              <Lock className="mr-2 h-3 w-3" />
              <span>Login to view full audience and performance data</span>
            </div>
          </div>
        )}

        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => handleInventorySelect && handleInventorySelect(billboard)}
          >
            Details
          </Button>
          {actuallyInCart ? (
            <Button
              onClick={() => handleRemoveFromCart && handleRemoveFromCart(billboard)}
              size="sm"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Remove
            </Button>
          ) : (
            (() => {
              let itemPrice = 0;
              if (billboard.type === 'DIGITAL') {
                itemPrice = priceFormat === 'spot-based'
                  ? billboard.perSpotPrice
                  : billboard.perDayPrice;
              } else {
                itemPrice = (billboard?.sellingRate?.monthly?.month1 && billboard.sellingRate.monthly.month1 > 0)
                  ? billboard.sellingRate.monthly.month1
                  : billboard.currentPrice;
              }
              const isPricedOut = !itemPrice || itemPrice === 0;
              return (
                <Button
                  onClick={() => handleAddToCart && handleAddToCart(billboard)}
                  size="sm"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={isPricedOut || !billboard.active}
                >
                  {isPricedOut ? 'Unavailable' : !billboard.active ? 'Sold Out' : 'Add to Cart'}
                </Button>
              );
            })()
          )}
        </div>

      </CardContent>
    </Card>
  );
}
